import { createClient } from "@sanity/client";

const projectId = process.env.REACT_APP_SANITY_PROJECT_ID || "ty3ch3ek";
const dataset = process.env.REACT_APP_SANITY_DATASET || "production";
const apiVersion = process.env.REACT_APP_SANITY_API_VERSION || "2026-05-20";

const sanityClient = createClient({
    projectId,
    dataset,
    apiVersion,
    useCdn: !process.env.REACT_APP_SANITY_TOKEN,
    token: process.env.REACT_APP_SANITY_TOKEN,
});

const LEGACY_ENTRANCE_ID = "3fBeBJyse5IOK0JqdTaUl";

const PHOTO_ENTRY_FIELDS = `
  _id,
  _type,
  _updatedAt,
  title,
  value,
  photos[]{
    ...,
    asset->{
      url,
      mimeType,
      originalFilename,
      metadata{
        dimensions{
          width,
          height
        }
      }
    }
  }
`;

const ENTRY_QUERIES = {
    categorieProjet: `*[_type == "categorieProjet"] | order(coalesce(integer, 0) asc, title asc){
    _id,
    _type,
    _updatedAt,
    title,
    titleEn,
    integer,
    slug,
    photoRef[]->{
      ${PHOTO_ENTRY_FIELDS}
    }
  }`,
    inSitu: `*[_type == "inSitu"] | order(coalesce(integer, 0) asc, title asc){
    _id,
    _type,
    _updatedAt,
    title,
    titleEn,
    integer,
    slug,
    photoRef[]->{
      ${PHOTO_ENTRY_FIELDS}
    }
  }`,
    informations: `*[_type == "informations"] | order(coalesce(integer, 0) asc, title asc){
    _id,
    _type,
    _updatedAt,
    title,
    titleEn,
    integer,
    body,
    image{
      ...,
      asset->{
        url,
        mimeType,
        originalFilename,
        metadata{
          dimensions{
            width,
            height
          }
        }
      }
    },
    pdf{
      asset->{
        url,
        mimeType,
        originalFilename
      }
    }
  }`,
};

const ENTRANCE_QUERY = `*[_type == "entrancePage"] | order(_createdAt asc)[0]{
  _id,
  _type,
  _updatedAt,
  title,
  photoRef[]->{
    ${PHOTO_ENTRY_FIELDS}
  }
}`;

const getLocalizedValue = (value, locale = "fr") => {
    if (value == null) return null;
    if (Array.isArray(value) || typeof value !== "object") return value;

    return (
        value[locale] ??
        value.fr ??
        value.en ??
        Object.values(value).find(
            (candidate) => typeof candidate === "string" && candidate.trim(),
        ) ??
        null
    );
};

const getLocalizedBlocks = (value, locale = "fr") => {
    if (!value) return null;
    if (Array.isArray(value)) return value.length > 0 ? value : null;

    const current = value[locale];
    if (Array.isArray(current) && current.length > 0) {
        return current;
    }

    const fallback = locale === "fr" ? value.en : value.fr;
    if (Array.isArray(fallback) && fallback.length > 0) {
        return fallback;
    }

    return (
        Object.values(value).find(
            (candidate) => Array.isArray(candidate) && candidate.length > 0,
        ) || null
    );
};

const portableTextToPlainText = (blocks = []) =>
    blocks
        .filter((block) => block?._type === "block" && Array.isArray(block.children))
        .map((block) =>
            block.children.map((child) => child?.text || "").join(""),
        )
        .join("\n\n")
        .trim();

const buildImageDetails = (asset) => {
    const width = asset?.metadata?.dimensions?.width;
    const height = asset?.metadata?.dimensions?.height;

    return width && height ? { image: { width, height } } : {};
};

const normalizeImageAsset = (image, locale) => {
    const asset = image?.asset;
    if (!asset?.url) return null;

    const localizedTitle = getLocalizedValue(image?.titre, locale) || image?.title;
    const localizedDescription = getLocalizedBlocks(image?.description, locale);
    const descriptionText = portableTextToPlainText(localizedDescription || []);

    return {
        sanitySource: image,
        fields: {
            title: localizedTitle || undefined,
            titre: localizedTitle || undefined,
            description: descriptionText || undefined,
            descriptionPortable: localizedDescription || undefined,
            file: {
                url: asset.url,
                contentType: asset.mimeType || "",
                fileName: asset.originalFilename || "",
                details: buildImageDetails(asset),
            },
        },
    };
};

const normalizeFileAsset = (file) => {
    const asset = file?.asset;
    if (!asset?.url) return null;

    return {
        fields: {
            file: {
                url: asset.url,
                contentType: asset.mimeType || "",
                fileName: asset.originalFilename || "",
                details: {},
            },
        },
    };
};

const normalizePhotoDocument = (document, locale) => {
    const normalizedAssets = (document?.photos || [])
        .map((image) => normalizeImageAsset(image, locale))
        .filter(Boolean);

    if (normalizedAssets.length === 0) {
        return null;
    }

    const leftDescriptionFr = getLocalizedBlocks(document?.photos?.[0]?.description, "fr");
    const leftDescriptionEn =
        getLocalizedBlocks(document?.photos?.[0]?.description, "en") || leftDescriptionFr;
    const rightDescriptionFr = getLocalizedBlocks(document?.photos?.[1]?.description, "fr");
    const rightDescriptionEn =
        getLocalizedBlocks(document?.photos?.[1]?.description, "en") || rightDescriptionFr;

    const fields = {
        title: document?.title || undefined,
        titre: document?.title || undefined,
        brightness: document?.value ?? 2.5,
        photos: normalizedAssets,
        descriptionFr: leftDescriptionFr || undefined,
        descriptionEn: leftDescriptionEn || undefined,
        descriptionPhotoGaucheFr: leftDescriptionFr || undefined,
        descriptionPhotoGaucheEn: leftDescriptionEn || undefined,
        descriptionPhotoDroiteFr: rightDescriptionFr || undefined,
        descriptionPhotoDroiteEn: rightDescriptionEn || undefined,
    };

    if (normalizedAssets.length === 1) {
        fields.photo = normalizedAssets[0];
    }

    return {
        sys: {
            id: document?._id,
            updatedAt: document?._updatedAt,
        },
        fields,
    };
};

const normalizeCategory = (document, locale) => {
    const normalizedPhotos = (document?.photoRef || [])
        .map((entry) => normalizePhotoDocument(entry, locale))
        .filter(Boolean);

    const localizedTitle = locale === "en" && document?.titleEn ? document.titleEn : document?.title;

    return {
        sys: {
            id: document?._id,
            updatedAt: document?._updatedAt,
        },
        fields: {
            title: localizedTitle || undefined,
            titre: localizedTitle || undefined,
            titleEn: document?.titleEn || undefined,
            titleFr: document?.title || undefined,
            ordre: document?.integer ?? 0,
            url: document?.slug?.current || "",
            toutLesProjets: normalizedPhotos,
        },
    };
};

const normalizeProject = (document, locale) => {
    const localizedTitle = locale === "en" && document?.titleEn ? document.titleEn : document?.title;

    return {
        sys: {
            id: document?._id,
            updatedAt: document?._updatedAt,
        },
        fields: {
            title: localizedTitle || undefined,
            titre: localizedTitle || undefined,
            titleEn: document?.titleEn || undefined,
            titleFr: document?.title || undefined,
            ordre: document?.integer ?? 0,
            url: document?.slug?.current || "",
            photos: (document?.photoRef || [])
                .map((entry) => normalizePhotoDocument(entry, locale))
                .filter(Boolean),
        },
    };
};

const normalizeInformation = (document, locale) => {
    const localizedTitle = locale === "en" && document?.titleEn ? document.titleEn : document?.title;

    return {
        sys: {
            id: document?._id,
            updatedAt: document?._updatedAt,
        },
        fields: {
            title: localizedTitle || undefined,
            titre: localizedTitle || undefined,
            titleEn: document?.titleEn || undefined,
            titleFr: document?.title || undefined,
            ordre: document?.integer ?? 0,
            texte: getLocalizedBlocks(document?.body, locale) || [],
            photoFond: normalizeImageAsset(document?.image, locale),
            pdf: normalizeFileAsset(document?.pdf),
        },
    };
};

const normalizeEntrancePage = (document, locale) => ({
    sys: {
        id: document?._id || LEGACY_ENTRANCE_ID,
        updatedAt: document?._updatedAt,
    },
    fields: {
        title: document?.title || undefined,
        titre: document?.title || undefined,
        photos: (document?.photoRef || [])
            .map((entry) => normalizePhotoDocument(entry, locale))
            .filter(Boolean),
    },
});

const NORMALIZERS = {
    categorieProjet: normalizeCategory,
    inSitu: normalizeProject,
    informations: normalizeInformation,
};

const client = {
    async getEntries({ content_type: contentType, locale = "fr" } = {}) {
        const query = ENTRY_QUERIES[contentType];
        const normalize = NORMALIZERS[contentType];

        if (!query || !normalize) {
            throw new Error(`Unsupported Sanity content type: ${contentType}`);
        }

        const documents = await sanityClient.fetch(query);

        return {
            items: documents.map((document) => normalize(document, locale)),
        };
    },

    async getEntry(entryId, { locale = "fr" } = {}) {
        if (entryId !== LEGACY_ENTRANCE_ID && entryId !== "entrancePage") {
            throw new Error(`Unsupported Sanity entry id: ${entryId}`);
        }

        const document = await sanityClient.fetch(ENTRANCE_QUERY);
        return normalizeEntrancePage(document, locale);
    },
};

export default client;